import React from 'react';
import {
  Button,
  Col,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Radio,
  Row,
  Switch,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
// Helpers
import {
  BoutFormValues,
  CompetitorMode,
  createEmptyBout,
  MAX_BOUTS,
} from 'pages/Events/matchcard';
// Components
import HostSelect from 'pages/Events/components/TicketEventForm/components/HostSelect/HostSelect';
// Styles
import styles from './MatchcardFields.module.scss';

const { Text } = Typography;

type CompetitorFieldProps = {
  /** Relative to the list item, for the fields. */
  name: (string | number)[];
  /** From the form root, for reading the chosen mode. */
  path: (string | number)[];
  corner: 'A' | 'B';
};

const CompetitorField = ({
  name,
  path,
  corner,
}: CompetitorFieldProps): JSX.Element => (
  <div className={styles.competitor}>
    <Text strong className={corner === 'A' ? styles.cornerA : styles.cornerB}>
      Competitor {corner}
    </Text>
    <Form.Item name={[...name, 'mode']} className={styles.mode}>
      <Radio.Group size="small" optionType="button">
        <Radio.Button value={CompetitorMode.Store}>On MILLIONS</Radio.Button>
        <Radio.Button value={CompetitorMode.Invite}>Invite</Radio.Button>
      </Radio.Group>
    </Form.Item>
    <Form.Item noStyle shouldUpdate>
      {({ getFieldValue }) =>
        getFieldValue([...path, 'mode']) === CompetitorMode.Invite ? (
          <>
            <Form.Item
              name={[...name, 'name']}
              label="Name"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: 'Name the fighter',
                },
              ]}
              className={styles.field}
            >
              <Input placeholder="Fighter name" />
            </Form.Item>
            <Form.Item
              name={[...name, 'invitedEmail']}
              label="Invite by email"
              extra="Optional. They get a link to claim the spot once the card is saved."
              rules={[{ type: 'email', message: 'Enter a valid email' }]}
              className={styles.field}
            >
              <Input placeholder="name@example.com" />
            </Form.Item>
          </>
        ) : (
          <Form.Item name={[...name, 'storeId']} className={styles.field}>
            <HostSelect
              placeholder="Search athletes, organizations, creators"
              aria-label={`Competitor ${corner}`}
            />
          </Form.Item>
        )
      }
    </Form.Item>
  </div>
);

type MatchcardFieldsProps = {
  form: FormInstance;
  name?: string;
};

/** The fight card editor, for any antd form that keeps the bouts under `name`. */
const MatchcardFields = ({
  form,
  name = 'bouts',
}: MatchcardFieldsProps): JSX.Element => {
  const makeMainEvent = (index: number): void => {
    const bouts: BoutFormValues[] = form.getFieldValue(name) || [];

    form.setFieldsValue({
      [name]: bouts.map((bout, boutIndex) => ({
        ...bout,
        isMainEvent: boutIndex === index ? !bout.isMainEvent : false,
      })),
    });
  };

  return (
    <Form.List name={name}>
      {(fields, { add, remove, move }) => (
        <div className={styles.root}>
          {fields.map((field, index) => (
            <div key={field.key} className={styles.bout} data-testid="bout">
              <div className={styles.boutHeader}>
                <Text strong>Bout {index + 1}</Text>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) =>
                    getFieldValue([name, field.name, 'isMainEvent']) ? (
                      <Tag color="gold">Main event</Tag>
                    ) : null
                  }
                </Form.Item>
                <div className={styles.boutActions}>
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => (
                      <Switch
                        size="small"
                        checked={Boolean(
                          getFieldValue([name, field.name, 'isMainEvent'])
                        )}
                        onChange={() => makeMainEvent(index)}
                        aria-label={`Bout ${index + 1} is the main event`}
                      />
                    )}
                  </Form.Item>
                  <Text type="secondary">Main event</Text>
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowUpOutlined />}
                    disabled={index === 0}
                    onClick={() => move(index, index - 1)}
                    aria-label={`Move bout ${index + 1} up`}
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowDownOutlined />}
                    disabled={index === fields.length - 1}
                    onClick={() => move(index, index + 1)}
                    aria-label={`Move bout ${index + 1} down`}
                  />
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(field.name)}
                    aria-label={`Delete bout ${index + 1}`}
                  />
                </div>
              </div>
              <Row gutter={16}>
                <Col xs={24} md={11}>
                  <CompetitorField
                    name={[field.name, 'competitorA']}
                    path={[name, field.name, 'competitorA']}
                    corner="A"
                  />
                </Col>
                <Col xs={24} md={2} className={styles.versus}>
                  <Text type="secondary">VS</Text>
                </Col>
                <Col xs={24} md={11}>
                  <CompetitorField
                    name={[field.name, 'competitorB']}
                    path={[name, field.name, 'competitorB']}
                    corner="B"
                  />
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name={[field.name, 'category']}
                    label="Weight class or title"
                    className={styles.field}
                  >
                    <Input placeholder="e.g. Lightweight title" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item
                    name={[field.name, 'rounds']}
                    label="Rounds"
                    rules={[{ type: 'integer', min: 1, message: 'At least 1' }]}
                    className={styles.field}
                  >
                    <InputNumber
                      min={1}
                      precision={0}
                      className={styles.number}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item
                    name={[field.name, 'roundMinutes']}
                    label="Minutes a round"
                    rules={[{ type: 'integer', min: 1, message: 'At least 1' }]}
                    className={styles.field}
                  >
                    <InputNumber
                      min={1}
                      precision={0}
                      className={styles.number}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          ))}
          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            disabled={fields.length >= MAX_BOUTS}
            onClick={() => add(createEmptyBout())}
          >
            Add bout
          </Button>
        </div>
      )}
    </Form.List>
  );
};

export default MatchcardFields;
